import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Api } from 'src/models/Api.model';
import { CriteriaGroupGroupElementsInnerOperatorEnum } from 'src/models/GalenData/CriteriaGroupGroupElementsInner.model';
import { OrderStatusEnum } from 'src/models/GalenData/Order.model';
import { getLocaleCode, Language } from 'src/models/Language.model';
import { CompletedReasonEnum, Session } from 'src/models/Session.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';

export default function useActionAnalyzeModel(
  handleCloseAnalyzingProgressModal: () => void,
  handleResetSession: () => void,
  session: Session,
) {
  const history = useHistory();

  const { tCommon, language } = useUiTranslation();
  const { handleHttpRequestError } = useHandleHttpRequestError();

  const getOrderStatus = (completedReason: CompletedReasonEnum) => {
    if (completedReason === CompletedReasonEnum.COMPLETED_EXAM) {
      return OrderStatusEnum.COMPLETE;
    }
    return OrderStatusEnum.CLOSED;
  };

  const [isClosingSession, setIsClosingSession] = useState(false);

  const handleCloseSession = async (completedReason: CompletedReasonEnum) => {
    try {
      setIsClosingSession(true);
      const [sessionData, orderData] = await Promise.all([
        Api.remote.load_session({
          deviceDataId: session.iid,
        }),
        Api.remote.get_exam_order({
          groupElements: [
            {
              key: 'OrderId',
              operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
              value: session.orderId,
            },
          ],
        }),
      ]);
      if (!sessionData) {
        throw new Error(tCommon('message.error.failedToLoadSession'));
      }

      if (sessionData.completedTime) {
        throw new Error(
          `${tCommon('message.error.sessionAlreadyCompleted')} ${tCommon(
            `examFields.completedReasonEnum.${completedReason}`,
          )}`,
        );
      }

      if (!orderData.data.content) {
        throw new Error(tCommon('message.error.orderNotFound'));
      }

      const orderStatus = getOrderStatus(completedReason);

      await Promise.all([
        Api.remote.save_session(session.iid, {
          completedReason,
          completedTime: new Date(),
        }),
        Api.remote.update_exam_order(orderData.data.content[0], {
          status: orderStatus,
        }),
      ]);

      handleResetSession();

      setIsClosingSession(false);

      history.push('/session-form');
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    } finally {
      handleCloseAnalyzingProgressModal();
    }
  };

  const handleCompleteExam = async () => {
    await handleCloseSession(CompletedReasonEnum.COMPLETED_EXAM);
  };

  const handleSaveAndDilate = () => {
    handleCloseAnalyzingProgressModal();
    handleResetSession();

    history.push('/results');
  };

  const handleEndSession = async () => {
    await handleCloseSession(CompletedReasonEnum.END_SESSION);
  };

  return {
    handleCompleteExam,
    handleSaveAndDilate,
    handleEndSession,
    isClosingSession,
  };
}
